import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { SearchResultCardLayout } from '../SearchResultCardLayout/SearchResultCardLayout'

import { ProgressBar, Text } from '@percent/lemonade'
import classes from './FundraiserCard.module.scss'
import fallbackLogo from '@percent/workplace-giving/assets/images/fallback-logo.svg'
import { SuggestedFundraisers } from '@percent/workplace-giving/api/fundraiser/suggestedFundraisers/suggestedFundraisers.types'
import { CampaignFundraiser } from '@percent/workplace-giving/api/campaigns/getCampaignFundraisers/getCampaignFundraisers.types'
import { formatMoneyShort } from '@percent/utility'
import { useOrganisationBadge } from '@percent/workplace-giving/common/hooks'

export type FundraiserCardProps = Readonly<{
  fundraiser: SuggestedFundraisers | CampaignFundraiser
  track?: VoidFunction
  linkAdditionalParams?: URLSearchParams
}>

export function FundraiserCard({ fundraiser, track, linkAdditionalParams }: FundraiserCardProps): JSX.Element {
  const { t, i18n } = useTranslation()
  const { badge, badgeTooltip } = useOrganisationBadge(fundraiser.organisation?.id)

  const linkTo = useMemo(() => {
    const basePath = `/fundraisers/${fundraiser.id}`
    const queryString = linkAdditionalParams ? `?${linkAdditionalParams.toString()}` : ''

    return `${basePath}${queryString}`
  }, [fundraiser.id, linkAdditionalParams])

  return (
    <SearchResultCardLayout
      title={fundraiser.title}
      linkTo={linkTo}
      handleTracking={() => track?.()}
      image={fundraiser.imageUrl}
      logo={fundraiser.organisation ? fundraiser.organisation?.imageUrl || fallbackLogo : undefined}
      logoTooltipText={fundraiser.organisation?.name}
      logoBadge={badge}
      logoBadgeTooltip={badgeTooltip}
    >
      <div className={classes.Details}>
        {fundraiser.goal !== null && (
          <>
            <ProgressBar
              progressPercentage={Math.min((fundraiser.total.amount / fundraiser.goal.amount) * 100, 100)}
              backgroundColorComplete={fundraiser.goal.amount >= fundraiser.total.amount ? 'primary' : 'success400'}
              backgroundColorIncomplete={fundraiser.goal.amount >= fundraiser.total.amount ? 'primary' : 'success400'}
              shadeColor="gray90"
              sizeVariant="x-small"
              enableAnimation
            />
            <Text as="p" size="small">
              {t('workplace_giving.fundraiser.totalRaisedOutOfGoalAmount', {
                totalAmount: formatMoneyShort(fundraiser.total, { locale: i18n.language }),
                goalAmount: formatMoneyShort(fundraiser.goal, { locale: i18n.language })
              })}
            </Text>
          </>
        )}
        {fundraiser.goal === null && (
          <Text as="p" size="small">
            {fundraiser.total.amount > 0 &&
              t('workplace_giving.fundraiser.amountRaised', {
                amount: formatMoneyShort(fundraiser.total, { locale: i18n.language })
              })}
            {fundraiser.total.amount === 0 && t('workplace_giving.fundraiser.beTheFirstToDonate')}
          </Text>
        )}
      </div>
    </SearchResultCardLayout>
  )
}
